// frontend/src/admin/pages/UserManagement.js
// ✅ VERSION CORRIGÉE - Classes CSS alignées avec UserManagement.css
import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Download,
  Eye,
  CreditCard,
  Filter,
  Ban,
  CheckCircle
} from 'lucide-react';

import '../styles/UserManagement.css';
import UserDetailModal from '../components/UserDetailModal';

// ✅ Client Axios commun
import api from '../../services/api';

const UserManagement = ({ adminUser, onPageChange }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    sort: 'created_at',
    order: 'DESC'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => {
    onPageChange?.('users');
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, filters]);

  const loadUsers = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        limit: 20,
        search: searchTerm,
        role: filters.role,
        status: filters.status,
        sort: filters.sort,
        order: filters.order
      };

      const response = await api.get('/admin/users', { params });
      const data = response.data;

      if (data.success) {
        setUsers(data.users || []);
        if (data.pagination) {
          setTotalPages(data.pagination.total_pages || 1);
          setTotalUsers(data.pagination.total_users || 0);
        } else {
          setTotalPages(1);
          setTotalUsers(data.users?.length || 0);
        }
      } else {
        alert(`Erreur: ${data.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
      alert('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    let [sortColumn, sortOrder] = value.split('_');

    const columnMapping = {
      created: 'created_at',
      last: 'last_login',
      total: 'total_credits',
      remaining: 'remaining_credits',
      email: 'email',
      username: 'username',
      role: 'role'
    };

    if (columnMapping[sortColumn]) {
      sortColumn = columnMapping[sortColumn];
    }

    setFilters((prev) => ({
      ...prev,
      sort: sortColumn,
      order: sortOrder.toUpperCase()
    }));
    setCurrentPage(1);
  };

  const viewUserDetails = (userId) => {
    setSelectedUserId(userId);
    setShowUserModal(true);
  };

  const addCredits = async (userId, credits) => {
    if (!credits || credits <= 0) {
      alert('Veuillez entrer un nombre de crédits valide.');
      return;
    }

    try {
      const response = await api.post(`/admin/users/${userId}/credits`, {
        credits,
        reason: 'Ajout manuel admin'
      });

      const data = response.data;
      if (data.success) {
        alert(`✅ ${credits} crédits ajoutés ! Nouveau total: ${data.new_total}`);
        loadUsers();
      } else {
        alert(`❌ Erreur: ${data.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur ajout crédits:', error);
      alert('Erreur lors de l\'ajout de crédits');
    }
  };

  // ✅ SUSPENDRE UN COMPTE
  const suspendUser = async (userId, username) => {
    if (
      !window.confirm(
        `⚠️ Suspendre le compte de ${username} ?\n\nL'utilisateur ne pourra plus se connecter.`
      )
    )
      return;

    try {
      const response = await api.post(`/admin/users/${userId}/suspend`);
      const data = response.data;

      if (data.success) {
        alert('✅ Compte suspendu avec succès');
        loadUsers();
      } else {
        alert(`❌ Erreur: ${data.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur suspension:', error);
      alert('❌ Erreur lors de la suspension du compte');
    }
  };

  // ✅ RÉACTIVER UN COMPTE
  const unsuspendUser = async (userId, username) => {
    if (!window.confirm(`✅ Réactiver le compte de ${username} ?`)) return;

    try {
      const response = await api.post(`/admin/users/${userId}/unsuspend`);
      const data = response.data;

      if (data.success) {
        alert('✅ Compte réactivé avec succès');
        loadUsers();
      } else {
        alert(`❌ Erreur: ${data.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur réactivation:', error);
      alert('❌ Erreur lors de la réactivation du compte');
    }
  };

  const exportCSV = async () => {
    try {
      const response = await api.get('/admin/users/export/csv', {
        responseType: 'blob'
      });

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Erreur lors de l\'export CSV');
      }
    } catch (error) {
      console.error('Erreur export CSV:', error);
      alert('Erreur lors de l\'export CSV');
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="admin-users-loading">
        <div className="admin-loading-spinner"></div>
        <h2>Chargement des utilisateurs…</h2>
      </div>
    );
  }

  return (
    <div className="admin-users-page">
      {/* ✅ Header - CSS OK */}
      <div className="admin-page-header">
        <div className="admin-page-title">
          <Users size={32} />
          <div>
            <h1>Gestion des Utilisateurs</h1>
            <p>{totalUsers} utilisateurs au total</p>
          </div>
        </div>

        <div className="admin-page-actions">
          <button
            className="admin-btn admin-btn--secondary"
            onClick={exportCSV}
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      {/* ✅ Bouton toggle filtres */}
      <div style={{ marginBottom: '1rem' }}>
        <button
          className="admin-btn admin-btn--secondary"
          onClick={() => setShowFilters((prev) => !prev)}
        >
          <Filter size={18} />
          {showFilters ? 'Masquer les filtres' : 'Afficher les filtres'}
        </button>
      </div>

      {/* ✅ Filtres - CSS CORRIGÉ */}
      {showFilters && (
        <div className="admin-filters-panel open">
          <div className="admin-search-bar">
            <Search size={18} />
            <input
              type="text"
              className="admin-search-input"
              placeholder="Rechercher par email ou pseudo…"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>

          <div className="admin-filters-row">
            <select
              className="admin-filter-select"
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <option value="">Tous les rôles</option>
              <option value="user">Utilisateur</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>

            <select
              className="admin-filter-select"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="suspended">Suspendu</option>
            </select>

            <select 
              className="admin-filter-select"
              value={`${filters.sort}_${filters.order.toLowerCase()}`} 
              onChange={handleSortChange}
            >
              <option value="created_desc">Date création (📉)</option>
              <option value="created_asc">Date création (📈)</option>
              <option value="last_desc">Dernière connexion (📉)</option>
              <option value="last_asc">Dernière connexion (📈)</option>
              <option value="email_asc">Email A→Z</option>
              <option value="email_desc">Email Z→A</option>
              <option value="total_desc">Crédits totaux (📉)</option>
              <option value="remaining_desc">Crédits restants (📉)</option>
            </select>
          </div>
        </div>
      )}

      {/* ✅ Table utilisateurs - CSS CORRIGÉ */}
      <div className="admin-users-table-container">
        <table className="admin-users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Utilisateur</th>
              <th>Rôle</th>
              <th>Crédits</th>
              <th>Statut</th>
              <th>Dernière connexion</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                  Aucun utilisateur trouvé.
                </td>
              </tr>
            )}

            {users.map((user) => (
              <tr key={user.id} className="admin-user-row">
                <td>{user.id}</td>
                
                {/* ✅ Info utilisateur avec avatar */}
                <td>
                  <div className="admin-user-info">
                    <div className="admin-user-avatar">
                      <Users size={20} />
                    </div>
                    <div className="admin-user-details">
                      <div className="admin-user-name">
                        {user.pseudo || user.username}
                      </div>
                      <div className="admin-user-email">
                        {user.email}
                      </div>
                    </div>
                  </div>
                </td>

                {/* ✅ Badge rôle */}
                <td>
                  <span className={`admin-role-badge admin-role-badge--${user.role}`}>
                    {user.role === 'super_admin' ? 'Super Admin' : 
                     user.role === 'admin' ? 'Admin' : 'User'}
                  </span>
                </td>

                {/* ✅ Crédits */}
                <td>
                  <div className="admin-credits-info">
                    <span className="admin-credits-remaining">
                      {user.remaining_credits ?? (user.total_credits - user.used_credits) ?? 0}
                    </span>
                    <span className="admin-credits-total">
                      / {user.total_credits ?? 0} total
                    </span>
                  </div>
                </td>

                {/* ✅ Statut compte */}
                <td>
                  {user.account_status === 'suspended' ? (
                    <span className="admin-status-badge" style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      color: '#ef4444'
                    }}>
                      Suspendu
                    </span>
                  ) : (
                    <span className="admin-status-badge" style={{
                      background: 'rgba(34, 197, 94, 0.1)',
                      color: '#22c55e'
                    }}>
                      Actif
                    </span>
                  )}
                </td>

                {/* Dernière connexion */}
                <td style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                  {user.last_login
                    ? new Date(user.last_login).toLocaleString('fr-FR')
                    : 'Jamais'}
                </td>

                {/* ✅ Actions - CSS CORRIGÉ */}
                <td>
                  <div className="admin-user-actions">
                    <button
                      className="admin-action-btn admin-action-btn--view"
                      title="Voir détails"
                      onClick={() => viewUserDetails(user.id)}
                    >
                      <Eye size={16} />
                    </button>

                    <button
                      className="admin-action-btn admin-action-btn--credits"
                      title="Ajouter des crédits"
                      onClick={() => {
                        const value = window.prompt(
                          `Combien de crédits ajouter à ${user.pseudo || user.username} ?`
                        );
                        const credits = parseInt(value, 10);
                        if (!isNaN(credits)) {
                          addCredits(user.id, credits);
                        }
                      }}
                    >
                      <CreditCard size={16} />
                    </button>

                    {user.account_status === 'suspended' ? (
                      <button
                        className="admin-action-btn admin-action-btn--unsuspend"
                        title="Réactiver le compte"
                        onClick={() => unsuspendUser(user.id, user.pseudo || user.username)}
                      >
                        <CheckCircle size={16} />
                      </button>
                    ) : (
                      <button
                        className="admin-action-btn admin-action-btn--suspend"
                        title="Suspendre le compte"
                        onClick={() => suspendUser(user.id, user.pseudo || user.username)}
                      >
                        <Ban size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ✅ Pagination - CSS OK */}
      <div className="admin-pagination">
        <div className="admin-pagination-info">
          Affichage de {users.length} utilisateur(s) sur {totalUsers}
        </div>
        
        <div className="admin-pagination-controls">
          <button
            className="admin-pagination-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            ← Précédent
          </button>
          
          <div className="admin-pagination-numbers">
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  className={`admin-pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            className="admin-pagination-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Suivant →
          </button>
        </div>
      </div>

      {/* ✅ Modal détails utilisateur */}
      {showUserModal && (
        <UserDetailModal
          userId={selectedUserId}
          onClose={() => setShowUserModal(false)}
          onRefresh={loadUsers}
        />
      )}
    </div>
  );
};

export default UserManagement;